import memoizeOne from 'memoize-one';
import React, { Component, Fragment } from 'react';
import { UserProductGroup } from '~/Api/User/ProductGroup';
import { ErrorCode, List, Product, ProductUpdate, ValidationResult } from '~/Api/User/ProductGroup/CzReimbursement';
import CopyGroupProductActionPanel from '~/CopyGroupProduct/CopyGroupProductActionPanel';
import ParametersPanel, { Values as ReferenceValues } from '~/CzReimbursement/ParametersPanel';
import ProductsTable, { ProductRow, ProductValues } from '~/CzReimbursement/ProductsTable/ProductsTable';
import ComplianceInfo from '~/CzSuklComplianceInfo';
import { parseDosage, parsePackageSize, PricedProduct, Product as GeneralProduct, ShowAllSwitch } from '~/Product';
import {
	CzechHideExcludedProductsTooltip,
	czechHideExcludedProductsLabel,
} from '~/libs/ui/molecules/ProductGroupShowAllItemsSwitch/czechHideExcludedProducts';
import ProductDetailModal, { PricesContainer } from '~/ProductDetailModal';
import ProductGroupRemoveProductModal from '~/ProductGroupRemoveProductModal';
import ProductInstructions from '~/ProductInstructions';
import ActivityIndicator from '~/UI/ActivityIndicator';
import Heading from '~/UI/Heading/Heading';
import { RefreshProductListAction } from '~/libs/ui/molecules/RefreshProductListAction';

import { ProductProvider } from './ProductProvider';
import { ReferencePriceInValues, ReferencePriceValue } from './ProductsTable/Row/Row';
import styles from './styles.less';

interface Props {
	header: React.ReactNode;
	group: UserProductGroup;
	czReimbursement: List | null;
	pricedProducts: PricedProduct<Product>[];
	czechProducts: ProductRow[];
	suklReferencedProducts: ProductRow[];
	otherProducts: ProductRow[];
	alternativeProductsCount: number;
	validationResult: ValidationResult | null;
	isFetching: boolean;
	detailProductId: string | null;
	detailProductTabName: string;
	currency: string | null;
	onFetch(group: UserProductGroup): Promise<void>;
	onPrune(group: UserProductGroup): void;
	productDetailLink(product: { id: string }): { pathname: string; search: string };
	onCloseDetail(): void;
	onShowDetail(productId: string, detailProductTabName: string): void;
	onUpdate(
		group: UserProductGroup,
		packageSize: number | null,
		dosageStrength: number | null,
		usualTherapeuticDailyDose: number | null,
		usualTherapeuticDailyDosePerPackage: number | null,
		products: ProductUpdate[]
	): void;
	onSave(group: UserProductGroup): void;
	onCurrencyChange(currency: string): void;
	onDeleteItems(userProductGroup: UserProductGroup, products: GeneralProduct[], onSuccess: () => void): Promise<void>;
	onResetProductList(groupId: string): void;
}

interface Values {
	reference: ReferenceValues;
	product: { [key: string]: ProductValues };
}

interface State {
	values: Values | null;
	lastSelectedProductId: string | null;
	lastSeenProductIdDetail: string | null;
	removeProduct: GeneralProduct | null;
	checkedRows: GeneralProduct[];
}

export default class CzReimbursement extends Component<Props, State> {
	static defaultProps = {
		czReimbursement: null,
		validationResult: null,
		detailProductId: null,
		detailProductTabName: 'info',
	};

	state: State = {
		values: null,
		lastSelectedProductId: null,
		lastSeenProductIdDetail: null,
		removeProduct: null,
		checkedRows: [],
	};

	savedBadgeComponent = null;

	componentDidMount() {
		const { group, onFetch } = this.props;
		onFetch(group);
	}

	componentWillUnmount() {
		const { group, onPrune } = this.props;
		onPrune(group);
	}

	componentDidUpdate(prevProps: Props) {
		const { group, validationResult, onFetch } = this.props;

		if (prevProps.group !== group) {
			onFetch(group);
		} else if (prevProps.validationResult !== validationResult) {
			this.processValidationResult(validationResult);
		}
	}

	resolveValues(): Values {
		const {
			props: { czReimbursement },
			state: { values },
		} = this;
		return this.createValues(czReimbursement, values);
	}

	private readonly checkRow = (product: GeneralProduct, checked: boolean) => {
		this.setState((prevState) => ({
			checkedRows: checked
				? [...prevState.checkedRows, product]
				: prevState.checkedRows.filter((row) => row.id !== product.id),
		}));
	};

	private readonly countRows = () => {
		return this.state.checkedRows.length;
	};

	onDeleteItems = async (group: UserProductGroup, products: GeneralProduct[]) => {
		await this.props.onDeleteItems(group, products, () => {
			this.setState({ checkedRows: [] });
		});
	};

	private readonly onClickCancelDelete = () => {
		this.setState({ checkedRows: [] });
	};

	createValues = memoizeOne((czReimbursement: List | null, values: Values | null): Values => {
		return {
			reference: values?.reference ?? {
				packageSize: {
					value: String(czReimbursement?.reference.packageSize ?? ''),
					isValid: true,
				},
				dosageStrength: {
					value: String(czReimbursement?.reference.dosageStrength ?? ''),
					isValid: true,
				},
				usualTherapeuticDailyDose: {
					value: String(czReimbursement?.reference.usualTherapeuticDailyDose ?? ''),
					isValid: true,
				},
				usualTherapeuticDailyDosePerPackage: {
					value: String(czReimbursement?.reference.usualTherapeuticDailyDosePerPackage ?? ''),
					isValid: true,
				},
			},
			product:
				czReimbursement?.products.reduce((products: { [key: string]: ProductValues }, product) => {
					const currentValues = values?.product[product.product.id];
					const hasSelectedValidReferencePrice =
						currentValues !== undefined &&
						product.referencePrices.find(
							(referencePrice) => referencePrice.calculatedPrice.type === currentValues.referencePrice.value?.value
						) !== undefined;
					const referencePrice = product.referencePrices.find((price) => price.calculatedPrice.selected);

					products[product.product.id] = {
						referencePrice: hasSelectedValidReferencePrice
							? currentValues.referencePrice
							: {
									value:
										referencePrice !== undefined
											? {
													value: referencePrice.calculatedPrice.type,
													parentValue: null,
													prompt: null,
											  }
											: null,
									isValid: product.referencePrices.length === 0 || referencePrice !== undefined,
							  },
						packageSizeForSingleUnit: currentValues?.packageSizeForSingleUnit ?? {
							value: product.packageSizeForSingleUnit !== null ? String(product.packageSizeForSingleUnit) : '',
							isValid: true,
						},
						selected: currentValues?.selected ?? product.selected,
					};
					return products;
				}, {}) || {},
		};
	});

	processValidationResult(validationResult: ValidationResult | null) {
		if (!validationResult) {
			return;
		}

		const values = this.resolveValues();
		const referenceValues = {
			...values.reference,
			packageSize: {
				...values.reference.packageSize,
				isValid: !validationResult.referenceSettingsIsMissing,
			},
			dosageStrength: {
				...values.reference.dosageStrength,
				isValid: !validationResult.referenceSettingsIsMissing,
			},
			usualTherapeuticDailyDose: {
				...values.reference.usualTherapeuticDailyDose,
				isValid: !validationResult.referenceSettingsIsMissing,
			},
			usualTherapeuticDailyDosePerPackage: {
				...values.reference.usualTherapeuticDailyDosePerPackage,
				isValid: !validationResult.referenceSettingsIsMissing,
			},
		};
		const productValues: { [key: string]: ProductValues } = { ...values.product };

		validationResult.productErrors.forEach((error) => {
			const values = productValues[error.productId || ''];
			if (!values) {
				return;
			}
			if (error.code === ErrorCode.RequiredProductSettingsMissingErrorCode) {
				productValues[error.productId || ''] = {
					...values,
					packageSizeForSingleUnit: {
						...values.packageSizeForSingleUnit,
						isValid: false,
					},
				};
			} else if (error.code === ErrorCode.IllegalPriceTypeSelectedErrorCode) {
				productValues[error.productId || ''] = {
					...values,
					referencePrice: { ...values.referencePrice, isValid: false },
				};
			}
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});
	}

	private handleProductsCopied = () => {
		this.setState((prevState) => ({
			checkedRows: [],
		}));
	};

	render() {
		const {
			props: {
				header,
				group,
				czReimbursement,
				pricedProducts,
				czechProducts,
				suklReferencedProducts,
				otherProducts,
				alternativeProductsCount,
				isFetching,
				detailProductId,
				productDetailLink,
				detailProductTabName,
				currency,
				onShowDetail,
				onSave,
				onCurrencyChange,
				onResetProductList,
			},
			state: { lastSelectedProductId, lastSeenProductIdDetail, removeProduct, checkedRows },
		} = this;

		if (czReimbursement === null && isFetching) {
			return <ActivityIndicator />;
		}

		const values = this.resolveValues();
		const usualTherapeuticDailyDosePerPackage = parsePackageSize(
			values.reference.usualTherapeuticDailyDosePerPackage.value
		).value;

		return (
			<Fragment>
				{header}

				<section className={styles.section}>
					{detailProductId ? (
						<PricesContainer currency={currency} productId={detailProductId}>
							<ProductProvider czReimbursement={czReimbursement} id={detailProductId}>
								{(product) => (
									<ProductDetailModal
										currency={currency}
										groupId={group.id}
										isFetching={false}
										product={product}
										productId={detailProductId}
										products={pricedProducts}
										selectedTabName={detailProductTabName}
										shareLink={`${window.location.origin}/search/${encodeURIComponent(detailProductId)}/${
											detailProductTabName === 'info' ? '' : encodeURIComponent(detailProductTabName)
										}`}
										status={this.renderCompliance(detailProductId)}
										onClose={this.handleCloseDetail}
										onCurrencyChange={onCurrencyChange}
										onChange={(productId) => onShowDetail(productId, detailProductTabName)}
										onSelectTab={(tabName) => onShowDetail(detailProductId, tabName)}
									/>
								)}
							</ProductProvider>
						</PricesContainer>
					) : null}

					<ParametersPanel
						group={group}
						values={values.reference}
						onSave={() => onSave(group)}
						onValueChange={this.referenceValueChanged}
					/>

					{czReimbursement?.products.length === 0 ? <ProductInstructions /> : null}

					{(czReimbursement?.products?.length || 0) > 0 ? (
						<>
							<div className='w-full'>
								<div className='w-full max-w-[1260px] mx-auto pt-5 pb-2 px-[40px] flex flex-wrap justify-between items-end'>
									<div className='w-full relative flex items-center justify-between z-1'>
										<Heading level={2}>Czech product</Heading>
										<div className='pl-3 text-right'>
											<RefreshProductListAction
												groupName={group.name}
												onConfirm={() => {
													this.setState({ values: null });
													onResetProductList(group.id);
												}}
											/>
										</div>
									</div>
								</div>
							</div>

							<ProductsTable
								detailLink={productDetailLink}
								highlightProductId={lastSeenProductIdDetail}
								checkedRows={checkedRows}
								checkRow={this.checkRow}
								lastSelectedProductId={lastSelectedProductId}
								products={czechProducts}
								productValues={values.product}
								referenceUsualTherapeuticDailyDosePerPackage={usualTherapeuticDailyDosePerPackage}
								onChange={this.handleProductValuesChange}
								onSave={() => onSave(group)}
								onSelect={this.productSelected}
							/>

							<div className={styles.tableHeader}>
								<div className={styles.in}>
									<Heading level={2}>European basket products</Heading>
									<ShowAllSwitch
										alternativeProductsCount={alternativeProductsCount}
										label={czechHideExcludedProductsLabel(alternativeProductsCount, 'do výpočtu pro stanovení úhrady')}
										tooltip={<CzechHideExcludedProductsTooltip />}
									/>
								</div>
							</div>
						</>
					) : null}

					{suklReferencedProducts.length > 0 ? (
						<ProductsTable
							detailLink={productDetailLink}
							highlightProductId={lastSeenProductIdDetail}
							checkedRows={checkedRows}
							checkRow={this.checkRow}
							lastSelectedProductId={lastSelectedProductId}
							products={suklReferencedProducts}
							productValues={values.product}
							referenceUsualTherapeuticDailyDosePerPackage={usualTherapeuticDailyDosePerPackage}
							onChange={this.handleProductValuesChange}
							onSave={() => onSave(group)}
							onSelect={this.productSelected}
						/>
					) : null}

					{otherProducts.length > 0 ? (
						<>
							<div className={styles.tableHeader}>
								<div className={styles.in}>
									<div>
										<Heading level={2}>Other</Heading>
									</div>
								</div>
							</div>

							<ProductsTable
								disableSelection
								detailLink={productDetailLink}
								highlightProductId={lastSeenProductIdDetail}
								checkedRows={checkedRows}
								checkRow={this.checkRow}
								lastSelectedProductId={lastSelectedProductId}
								products={otherProducts}
								productValues={values.product}
								referenceUsualTherapeuticDailyDosePerPackage={usualTherapeuticDailyDosePerPackage}
								onChange={this.handleProductValuesChange}
								onSave={() => onSave(group)}
								onSelect={this.productSelected}
							/>
						</>
					) : null}

					{checkedRows.length > 0 ? (
						<CopyGroupProductActionPanel
							group={group}
							products={checkedRows}
							selectedProductsCount={this.countRows()}
							onClickCancelDelete={this.onClickCancelDelete}
							onClickRemoveRow={this.onDeleteItems}
							onProductsCopied={this.handleProductsCopied}
						/>
					) : null}

					{removeProduct ? (
						<ProductGroupRemoveProductModal
							group={group}
							product={removeProduct}
							onClose={() => this.setState({ removeProduct: null })}
							onConfirm={() => onSave(group)}
							onSuccess={() => this.setState({ removeProduct: null })}
						/>
					) : null}
				</section>
			</Fragment>
		);
	}

	renderCompliance(productId: string) {
		const { czReimbursement } = this.props;
		const product = czReimbursement?.products.find((product) => product.product.id === productId);
		return product ? <ComplianceInfo compliance={product.productCompliance} /> : <Fragment />;
	}

	referenceValueChanged = (fieldName: string, value: any) => {
		const values = this.resolveValues();
		const { reference } = values;
		this.setState(
			{
				values: {
					...values,
					reference: {
						...reference,
						packageSize: { ...reference.packageSize, isValid: null },
						dosageStrength: { ...reference.dosageStrength, isValid: null },
						usualTherapeuticDailyDose: {
							...reference.usualTherapeuticDailyDose,
							isValid: null,
						},
						usualTherapeuticDailyDosePerPackage: {
							...reference.usualTherapeuticDailyDosePerPackage,
							isValid: null,
						},
						[fieldName]: { value, isValid: null },
					},
				},
			},
			this.update
		);
	};

	handleProductValuesChange = (
		item: Product,
		referencePrice: ReferencePriceInValues | ReferencePriceValue | null,
		packageSizeForSingleUnit: string
	) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const invokeSave = currentValue.referencePrice !== referencePrice;

		const defaultReferencePrice = referencePrice || {};

		this.setState(
			{
				values: {
					reference: values.reference,
					product: {
						...values.product,
						[item.product.id]: {
							referencePrice: {
								value: {
									value: '',
									parentValue: null,
									prompt: null,
									...defaultReferencePrice,
								},
								isValid: Boolean(item.referencePrices.length === 0 || referencePrice !== null),
							},
							packageSizeForSingleUnit: {
								value: packageSizeForSingleUnit,
								isValid: false,
							},
							selected: currentValue.selected,
						},
					},
				},
			},
			() => {
				this.update();
				if (invokeSave) {
					this.props.onSave(this.props.group);
				}
			}
		);
	};

	productSelected = (item: Product, selected: boolean, range: boolean) => {
		const values = this.resolveValues();
		const currentValue = values.product[item.product.id];
		const productValues = { ...values.product };

		if (range) {
			this.selectProductRange(productValues, item.product.id, this.state.lastSelectedProductId, selected);
		} else {
			productValues[item.product.id] = { ...currentValue, selected };
		}

		this.setState(
			{
				values: { reference: values.reference, product: productValues },
				lastSelectedProductId: item.product.id,
			},
			this.update
		);
	};

	selectProductRange(
		productValues: { [key: string]: ProductValues },
		selectedProductId: string,
		lastSelectedProductId: string | null,
		selected: boolean
	) {
		const { pricedProducts } = this.props;
		const selectedIndex = pricedProducts.findIndex((pricedProduct) => pricedProduct.product.id === selectedProductId);
		const lastSelectedIndex = pricedProducts.findIndex(
			(pricedProduct) => pricedProduct.product.id === lastSelectedProductId
		);

		if (selectedIndex >= 0 && lastSelectedIndex >= 0) {
			const startIndex = Math.min(selectedIndex, lastSelectedIndex);
			const endIndex = Math.max(selectedIndex, lastSelectedIndex);
			for (let i = startIndex; i <= endIndex; i += 1) {
				const values = productValues[pricedProducts[i].product.id];
				productValues[pricedProducts[i].product.id] = { ...values, selected };
			}
		}
	}

	update = () => {
		const values = this.resolveValues();
		const referenceValues = { ...values.reference };
		const productValues: { [key: string]: ProductValues } = { ...values.product };

		const packageSize = parsePackageSize(referenceValues.packageSize.value);
		referenceValues.packageSize.isValid = packageSize.isValid;

		const dosageStrength = parseDosage(referenceValues.dosageStrength.value);
		referenceValues.packageSize.isValid = dosageStrength.isValid;

		const usualTherapeuticDailyDose = parseDosage(referenceValues.usualTherapeuticDailyDose.value);
		referenceValues.packageSize.isValid = usualTherapeuticDailyDose.isValid;

		const usualTherapeuticDailyDosePerPackage = parseDosage(referenceValues.usualTherapeuticDailyDosePerPackage.value);
		referenceValues.packageSize.isValid = usualTherapeuticDailyDosePerPackage.isValid;

		let hasValidationError = packageSize.isValid === false;

		const products: ProductUpdate[] = [];

		Object.keys(productValues).forEach((productId) => {
			const values = productValues[productId];
			const { referencePrice, packageSizeForSingleUnit } = values;
			const parsedPackageSizeForSingleUnit = parsePackageSize(packageSizeForSingleUnit.value);

			hasValidationError =
				hasValidationError || !referencePrice.isValid || parsedPackageSizeForSingleUnit.isValid === false;

			productValues[productId] = {
				...values,
				packageSizeForSingleUnit: {
					...values.packageSizeForSingleUnit,
					isValid: Boolean(parsedPackageSizeForSingleUnit.isValid),
				},
			};

			products.push(
				new ProductUpdate(
					productId,
					referencePrice.value?.value || null,
					parsedPackageSizeForSingleUnit.value,
					values.selected
				)
			);
		});

		this.setState({
			values: { reference: referenceValues, product: productValues },
		});

		if (!hasValidationError) {
			this.props.onUpdate(
				this.props.group,
				packageSize.value,
				dosageStrength.value,
				usualTherapeuticDailyDose.value,
				usualTherapeuticDailyDosePerPackage.value,
				products
			);
		}
	};

	handleCloseDetail = () => {
		const { detailProductId, onCloseDetail } = this.props;

		this.setState({ lastSeenProductIdDetail: detailProductId });
		onCloseDetail();
	};
}
